import {Component, inject} from '@angular/core';
import {LoadingService} from "../../../../../../core/services/loading.service";
import {EditModeService} from "../../../../../../core/services/edit-mode.service";
import {BaseModalComponent} from "../../../../../../core/components/modals/base-modal/base-modal.component";
import {MAT_DIALOG_DATA} from "@angular/material/dialog";
import {FormGroup} from "@angular/forms";
import {GetDataInModalParams} from "../../../types/product-card-modals.types";
import {TpTurnkeyCostService} from "./services/tp-turnkey-cost.service";
import {TurnkeyCostData, TurnkeyCostDialogData, TurnkeyCostFormField} from "./types/tp-turnkey-cost-modal,types";
import {defaultTurnkeyCostData} from "./configs/default-turnkey-cost-data";
import {turnkeyCostFields} from "./configs/turnkey-cost-fields";
import {TpTurnkeyCostPathStateService} from "./services/tp-turnkey-cost-path-state.service";
import {titleModal} from "./configs/turnkey-cost-modal-config";
import {
  MatAccordion,
  MatExpansionPanel,
  MatExpansionPanelHeader,
  MatExpansionPanelTitle
} from "@angular/material/expansion";
import {MatButton} from "@angular/material/button";
import {customDateProvider} from "../../../../../../core/locale/providers/custom-date.provider";
import {TurnkeyCostFormComponent} from "./components/turnkey-cost-form/turnkey-cost-form.component";
import {
  TurnkeyCostAccordionHeaderComponent
} from "./components/turnkey-cost-accordion-header/turnkey-cost-accordion-header.component";
import {TurnkeyCostFilesComponent} from "./components/turnkey-cost-files/turnkey-cost-files.component";
import {LoaderComponent} from "../../../../../../shared/components/loader/loader.component";

@Component({
  selector: 'app-tp-turnkey-cost-modal',
  imports: [
    BaseModalComponent,
    MatAccordion,
    MatButton,
    MatExpansionPanel,
    MatExpansionPanelHeader,
    MatExpansionPanelTitle,
    TurnkeyCostFormComponent,
    TurnkeyCostAccordionHeaderComponent,
    TurnkeyCostFilesComponent,
    LoaderComponent
  ],
  templateUrl: './tp-turnkey-cost-modal.component.html',
  styleUrl: './tp-turnkey-cost-modal.component.scss',
  providers: [ ...customDateProvider, LoadingService, EditModeService ]
})
export class TpTurnkeyCostModalComponent  extends BaseModalComponent {
  public turnkeyCostDialogData = inject<TurnkeyCostDialogData>(MAT_DIALOG_DATA);
  private tpTurnkeyCostService: TpTurnkeyCostService = inject(TpTurnkeyCostService);
  private tpTurnkeyCostPathStateService: TpTurnkeyCostPathStateService = inject(TpTurnkeyCostPathStateService);

  public turnkeyCostFormGroups: Array<FormGroup> = [];
  public turnkeyCostsData: Array<TurnkeyCostData> = [];
  public isValid: boolean = false;
  public noFirstData: boolean = false;

  public override async ngOnInit(): Promise<void> {
    super.ngOnInit();
    await this.initComponent();
  }

  public override ngOnDestroy(): void {
    super.ngOnDestroy();
    this.modalStatesService.closeModal(this.turnkeyCostDialogData.modalKey);
  }

  public getTitleModal(): string {
    return titleModal[this.turnkeyCostDialogData.modalKey];
  }

  public handleAddNew(): void {
    const formGroup = this.baseModalService.getFormGroup<TurnkeyCostFormField>(turnkeyCostFields);
    this.turnkeyCostFormGroups.push(formGroup);
    this.turnkeyCostsData.push(defaultTurnkeyCostData);
    this.isValid = false;
  }

  public updateTurnkeyCostData(data: TurnkeyCostData, index: number): void {
    data.date = this.utilityService.convertToDate(data.date);
    this.turnkeyCostsData[index] = data;
    this.turnkeyCostFormGroups.at(index)?.patchValue(data);
    this.isValid = !!data.id;
    this.noFirstData = !!this.turnkeyCostsData.length;
  }

  public deleteTurnkeyCost(index: number): void {
    this.turnkeyCostsData.splice(index, 1);
    this.turnkeyCostFormGroups.splice(index, 1);
    this.isValid = true;
  }

  public removeTurnkeyCostAtIndex(index: number): void {
    this.turnkeyCostFormGroups.splice(index, 1);
    this.turnkeyCostsData.splice(index, 1);

    if (!this.turnkeyCostsData.length) {
      this.noFirstData = false;
      this.handleAddNew();
    }
  }

  public setLoadingState(isLoading: boolean): void {
    this.loadingService.setIsLoadingState(isLoading);
  }

  private async initComponent(): Promise<void> {
    this.modalStatesService.openModal(this.turnkeyCostDialogData.modalKey);
    this.tpTurnkeyCostPathStateService.setPathState(this.turnkeyCostDialogData.modalKey);
    await this.fetchTurnkeyCostData();
    this.isValid = !!this.turnkeyCostsData.at(0)?.id;
    this.noFirstData = !!this.turnkeyCostsData.at(0)?.id;
  }

  private async fetchTurnkeyCostData(): Promise<void> {
    const params: GetDataInModalParams = {
      path: this.tpTurnkeyCostPathStateService.pathState(),
      queryParams: {
        technicalParametersId: this.turnkeyCostDialogData.parentId
      }
    };

    this.turnkeyCostsData = await this.tpTurnkeyCostService.getDataInModal<TurnkeyCostData>(params).catch(() => []);
    this.turnkeyCostsData.forEach(item => {
      item.date = this.utilityService.convertToDate(item.date);
    });

    this.initForm();
  }

  private initForm(): void {
    if (!this.turnkeyCostsData.length) {
      this.handleAddNew();
      return;
    }

    this.turnkeyCostFormGroups = this.turnkeyCostsData.map(data => {
      const formGroup = this.baseModalService.getFormGroup<TurnkeyCostFormField>(turnkeyCostFields);
      formGroup.patchValue(data);
      return formGroup;
    });
  }
}
